import BaseApi from 'api/BaseApi';
import { LayoutConfigurationReferenceType } from 'enums/LayoutConfigurationReferenceType';
import { IFormulaCondition } from 'api/digifi/FormulaConditionApi';
import { IFormulaBasedValidationRule } from 'api/digifi/FormulaBasedValidationRuleApi';

export interface IBaseCard {
  id: string;
  name: string;
  position: number;
  organizationId: string;
  organizationVersion: number | null;
  group: string;
  description?: string | null;
}

export interface IBaseGroup {
  id: string;
  position: number;
  organizationId: string;
  organizationVersion: number;
}

export interface IConditionalFormattingRule {
  formulaCondition: IFormulaCondition;
  color: string;
}

export interface IBaseVariableConfiguration {
  id: string;
  variable: string;
  position: number;
  cardId: string;
  default?: boolean;
  required?: boolean;
  organizationVersion: number;
  organizationId: string;
  validationRule?: IFormulaBasedValidationRule | null;
  conditionalDisplayRule?: IFormulaCondition | null;
  conditionalFormattingRules?: IConditionalFormattingRule[];
  failedFormattingConditionColor?: string | null;
}

export interface IBaseLayout<
  GroupType extends IBaseGroup = IBaseGroup,
  CardType extends IBaseCard = IBaseCard,
  VariableType extends IBaseVariableConfiguration = IBaseVariableConfiguration
> {
  groups: GroupType[];
  cards: CardType[];
  variables: VariableType[];
}

export interface IFindLayoutConfigurationParams {
  referenceType: LayoutConfigurationReferenceType;
  reference?: string;
}

export interface ILayoutConfigurationApi<
  GroupType extends IBaseGroup = IBaseGroup,
  CardType extends IBaseCard = IBaseCard,
  VariableType extends IBaseVariableConfiguration = IBaseVariableConfiguration
> {
  find(params: IFindLayoutConfigurationParams): Promise<IBaseLayout<GroupType, CardType, VariableType>>;
}

export default class LayoutConfigurationRestApi<
  GroupType extends IBaseGroup = IBaseGroup,
  CardType extends IBaseCard = IBaseCard,
  VariableType extends IBaseVariableConfiguration = IBaseVariableConfiguration
> extends BaseApi implements ILayoutConfigurationApi<GroupType, CardType, VariableType> {
  protected resourceName = 'layout-configuration';

  public find(params: IFindLayoutConfigurationParams): Promise<IBaseLayout<GroupType, CardType, VariableType>> {
    const searchParams = new URLSearchParams();

    searchParams.set('referenceType', params.referenceType);

    if (params.reference) {
      searchParams.set('reference', params.reference);
    }

    return this.fetch(`/${this.resourceName}?${searchParams}`);
  }
}
